import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ICONS } from 'components/Icon';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import { dateToNumber } from 'lib/riskbase';
import React from 'react';
import { useDispatch } from 'react-redux';
import { GlobalFieldRepeatableInstanceScreen } from 'screens/FieldComponents/GlobalFieldRepeatableInstanceScreen';
import {
  ASSET_SAVE_REPEATABLE_FIELDS,
  doAssetDeleteFieldInstance,
} from 'store/actions';
import {
  selectAssetFieldRepeatableInstancesForGroup,
  selectAssetOrganisationId,
} from 'store/assets/selectors';
import {
  selectUserOrganisationFeatures,
  selectUserOrganisationUuidToUid,
} from 'store/auth/selectors';
import { selectOrganisationFieldGroup } from 'store/organisations/selectors';
import { PinNew } from 'store/plan/types';
import { AppContextEnum, FieldResult } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSETFIELDREPEATABLE],
  Screens.ASSETFIELDREPEATABLE_INSTANCE
>;

export const FieldRepeatableInstanceScreen: React.FC<Props> = ({
  route,
  navigation,
}) => {
  const dispatch = useDispatch();

  const { assetId, instanceId, groupId } = route.params;

  const organisationId = useSelector((state) =>
    selectAssetOrganisationId(state, assetId),
  );

  const organisationUid = useSelector((state) =>
    selectUserOrganisationUuidToUid(state, organisationId),
  );
  const userFeatures = useSelector((state) =>
    selectUserOrganisationFeatures(state, organisationUid),
  );

  const allInstances = useSelector((state) =>
    selectAssetFieldRepeatableInstancesForGroup(state, assetId, groupId),
  );

  const group = useSelector((state) =>
    selectOrganisationFieldGroup(state, organisationId, groupId),
  );

  const instance = allInstances[instanceId];

  const otherInstances = React.useMemo(
    () =>
      instanceId
        ? Object.values(allInstances).filter(
            ({ instanceId: otherInstanceId }) => otherInstanceId !== instanceId,
          )
        : Object.values(allInstances),
    [instanceId, allInstances],
  );

  const handleSave = React.useCallback(
    (results: Record<string, FieldResult>) =>
      instance &&
      dispatch(
        ASSET_SAVE_REPEATABLE_FIELDS({
          assetId,
          instance: {
            ...instance,
            // autoId needs to be here until customer data is up-to-date
            autoId: instance.autoId || 1,
            updatedTs: dateToNumber(),
            results: {
              ...instance.results,
              ...results,
            },
          },
        }),
      ),
    [dispatch, assetId, instance],
  );

  const handleChange = React.useCallback(
    (
      fieldId: string,
      value: string | string[] | null,
      pin?: PinNew | '' | null,
    ) =>
      handleSave({
        [fieldId]: {
          fieldId,
          ts: dateToNumber(),
          value: value || value === '0' ? value : '',
          pin: pin || '',
          action: null,
        },
      }),
    [handleSave],
  );

  React.useEffect(() => {
    if (!instance) {
      navigation.goBack();
    }
  }, [instance, navigation]);

  if (!group || !instance) {
    return null;
  }

  return (
    <GlobalFieldRepeatableInstanceScreen
      reference={instance.reference}
      context={{
        context: AppContextEnum.localAsset,
        assetId,
      }}
      isInModal={false}
      group={group}
      fields={group.fields}
      id={instance.instanceId}
      editableFieldIds={userFeatures.editassetfields ? group.fieldIds : []}
      autoId={instance.autoId!}
      results={instance.results}
      fieldKeys={{}}
      autoIdNext={0}
      onChange={handleChange}
      showInvalid={false}
      otherInstances={otherInstances}
      onPressBackButton={navigation.goBack}
      headerMenuRight={
        userFeatures.editassetfields
          ? {
              iconButtonConfig: {
                icon: ICONS.EllipsisLight,
              },
              options: [
                {
                  text: `Delete ${group.calculatedInfo.singularName}`,
                  icon: ICONS.TrashCanLight,
                  onPress: () =>
                    dispatch(
                      doAssetDeleteFieldInstance({
                        assetId,
                        instanceId,
                      }),
                    ),
                  destructive: true,
                },
              ],
            }
          : undefined
      }
    />
  );
};
